import { useState, useEffect, useMemo } from 'react';
import { addMonths } from 'date-fns';
import api from 'state/axios';
import {
  Box,
  Typography,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';


const cellColor = (count, max) => {
  if (!count) return null;
  if (count >= max * 0.7) return '#d32f2f';
  if (count >= max * 0.3) return '#f57c00';
  return '#388e3c';
};

const ExpiryHeatmap = ({ matrixData, lookAheadMonths, onCellClick }) => {
  const [rankPriorityMap, setRankPriorityMap] = useState({});

  useEffect(() => {
    api.get('/ranks').then((res) => {
      const map = {};
      for (const r of res.data || []) map[r.rankCode] = r.rankPriority ?? 999;
      setRankPriorityMap(map);
    }).catch(() => {});
  }, []);

  const heatmap = useMemo(() => {
    if (!matrixData) return null;
    const { subjects, employees } = matrixData;
    const now = new Date();
    const horizon = addMonths(now, lookAheadMonths || 3);

    const rankSet = new Set(employees.map((e) => e.rank).filter(Boolean));
    const ranks = [...rankSet].sort(
      (a, b) => (rankPriorityMap[a] ?? 999) - (rankPriorityMap[b] ?? 999)
    );

    const grid = subjects.map((s) => {
      const sid = s._id.toString();
      const byRank = {};
      for (const rank of ranks) byRank[rank] = 0;

      for (const e of employees) {
        const qual = e.qualifications?.[sid];
        const needsTraining =
          !qual ||
          qual.status === 'missing' ||
          (qual.expiryDate && new Date(qual.expiryDate) <= horizon);
        if (needsTraining && e.rank) byRank[e.rank] = (byRank[e.rank] || 0) + 1;
      }
      return { subject: s, byRank };
    });

    const maxCount = Math.max(1, ...grid.flatMap((row) => Object.values(row.byRank)));
    return { grid, ranks, maxCount };
  }, [matrixData, lookAheadMonths, rankPriorityMap]);

  if (!heatmap) return null;

  const { grid, ranks, maxCount } = heatmap;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Typography variant="subtitle2" mb={1} color="text.secondary">
        Employees needing training in next {lookAheadMonths} months - by subject &amp; rank
      </Typography>
      <Box sx={{ overflowX: 'auto', width: '100%' }}>
        <Table size="small" sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                minWidth: 30, fontWeight: 'bold', fontSize: 12,
                position: 'sticky', left: 0, zIndex: 3,
                bgcolor: 'background.paper', borderBottomWidth: 2,
              }}>
                Subject
              </TableCell>
              {ranks.map((rank) => (
                <TableCell key={rank} align="center" sx={{
                  minWidth: 0, fontSize: 12, fontWeight: 'bold',
                  whiteSpace: 'nowrap', borderBottomWidth: 2,
                }}>
                  {rank}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {grid.map(({ subject, byRank }) => (
              <TableRow key={subject._id}>
                <TableCell sx={{
                  position: 'sticky', left: 0, zIndex: 1,
                  bgcolor: 'background.paper', py: 0.75, minWidth: 30,
                }}>
                  <Typography variant="caption" fontWeight="bold" display="block">
                    {subject.subjectCode}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {subject.subjectName}
                  </Typography>
                </TableCell>
                {ranks.map((rank) => {
                  const count = byRank[rank] || 0;
                  const bg = cellColor(count, maxCount);
                  return (
                    <TableCell
                      key={rank}
                      align="center"
                      onClick={() => count > 0 && onCellClick?.({ subject, rank, count })}
                      sx={{ cursor: count > 0 ? 'pointer' : 'default', py: 0.75, px: 0.5 }}
                    >
                      {count > 0 ? (
                        <Tooltip title={`${count} employee(s) needing training`}>
                          <Box sx={{
                            bgcolor: bg,
                            color: '#fff',
                            borderRadius: 1,
                            px: 0.5, py: 0.25,
                            fontSize: 12, fontWeight: 'bold',
                            display: 'inline-block', lineHeight: 1.4,
                          }}>
                            {count}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.disabled">-</Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default ExpiryHeatmap;
